"use client";

import { useEffect, useRef, useState } from "react";

export type HowItWorksStep = {
  title: string;
  desc: string;
  imageSrc: string;
  imageAlt: string;
};

type HowItWorksStepsProps = {
  steps: HowItWorksStep[];
};

const HowItWorksSteps = ({ steps }: HowItWorksStepsProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const stepRefs = useRef<(HTMLElement | null)[]>([]);
  const ratiosRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    const elements = stepRefs.current.filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number((entry.target as HTMLElement).dataset.stepIndex);
          if (Number.isNaN(index)) continue;
          ratiosRef.current.set(index, entry.intersectionRatio);
        }

        let bestIndex = 0;
        let bestRatio = 0;
        ratiosRef.current.forEach((ratio, index) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });

        if (bestRatio > 0) {
          setActiveIndex(bestIndex);
        }
      },
      {
        rootMargin: "-12% 0px -38% 0px",
        threshold: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1],
      },
    );

    for (const element of elements) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [steps.length]);

  return (
    <div className="alt-how-it-works__steps relative">
      <div aria-hidden="true" className="alt-how-it-works__timeline" />
      {steps.map((step, i) => {
        const isActive = i === activeIndex;

        return (
          <article
            className={`alt-how-it-works__step swiss-grid relative py-6 md:py-10${isActive ? " is-active" : ""}`}
            data-step-index={i}
            key={step.title}
            ref={(el) => {
              stepRefs.current[i] = el;
            }}
          >
            <div className="col-span-12 md:col-span-5 lg:col-span-5">
              <div className="alt-how-it-works__step-header">
                <span aria-hidden="true" className="alt-how-it-works__step-dot bg-amber" />
                <span className="alt-how-it-works__step-num type-display-md">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="type-display-md text-champagne-white alt-how-it-works__step-title">
                  {step.title}
                </h3>
                <p className="type-body-lg text-champagne-white/75 alt-how-it-works__step-desc">
                  {step.desc}
                </p>
              </div>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
              <div className="alt-how-it-works__step-media">
                {/* eslint-disable-next-line @next/next/no-img-element -- static marketing asset */}
                <img
                  alt={step.imageAlt}
                  className="alt-how-it-works__step-img"
                  decoding="async"
                  src={step.imageSrc}
                />
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default HowItWorksSteps;
